import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  IPackagingStockCountDetailedItem,
  IPackagingStockCountListItem,
} from 'src/types/packaging-stock-count';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  stockCounts: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IPackagingStockCountListItem[];
  };
  stockCount: IPackagingStockCountDetailedItem | null;
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  stockCounts: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  stockCount: null,
};

const packagingStockCountSlice = createSlice({
  name: 'packagingStockCount',
  initialState,
  reducers: {
    removePackagingStockCount: (state, action) => {
      const { stockCountId } = action.payload;

      const index = state.stockCounts.items.findIndex(
        (item) => item._id === stockCountId
      );

      if (index > -1) {
        action.payload.index = index;
        state.stockCounts.items = state.stockCounts.items.filter(
          (item) => item._id !== stockCountId
        );
      }
    },
    rollbackPackagingStockCountDeletion: (state, action) => {
      const { item, index } = action.payload;

      state.stockCounts.items.splice(index, 0, item);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPackagingStockCounts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPackagingStockCounts.fulfilled, (state, action) => {
        state.status = 'success';
        state.stockCounts = action.payload;
      })
      .addCase(getPackagingStockCounts.rejected, (state, action) => {
        state.stockCounts = initialState.stockCounts;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getPackagingStockCountById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPackagingStockCountById.fulfilled, (state, action) => {
        state.status = 'success';
        state.stockCount = action.payload;
        state.error = initialState.error;
      })
      .addCase(getPackagingStockCountById.rejected, (state, action) => {
        state.stockCount = initialState.stockCount;
        state.status = 'failure';
        state.error = action.error.message;
      });
  },
});

export default packagingStockCountSlice.reducer;

export const { removePackagingStockCount, rollbackPackagingStockCountDeletion } =
  packagingStockCountSlice.actions;

export const getPackagingStockCounts = createAsyncThunk(
  'packagingStockCount/paginated',
  async (params: {
    page: number;
    limit: number;
    generalSearch?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    sortBy?: string | null;
    sort?: 'asc' | 'desc' | null;
  }) => {
    const { page, limit, generalSearch, startDate, endDate, sortBy, sort } =
      params;

    try {
      const response = await axios.get(
        `packaging/stock-count?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&startDate=${startDate ? startDate.toISOString() : ''}&endDate=${
          endDate ? endDate.toISOString() : ''
        }&sortBy=${sortBy}&sort=${sort ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getPackagingStockCountById = createAsyncThunk(
  'packagingStockCount/getById',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`packaging/stock-count/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deletePackagingStockCount = createAsyncThunk(
  'packagingStockCount/delete',
  async (
    params: { item: IPackagingStockCountListItem; index: number },
    { dispatch }
  ) => {
    const { item, index } = params;

    try {
      const response = await axios.delete(`packaging/stock-count/${item._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackPackagingStockCountDeletion({ item, index }));
      throw err;
    }
  }
);

export interface TPackagingStockCountItemInput {
  packagingItem: string;
  addedQuantity: number;
}

export interface TCreatePackagingStockCount {
  countDate: Date;
  remarks: string | null;
  items: TPackagingStockCountItemInput[];
}

export const createPackagingStockCount = createAsyncThunk(
  'packagingStockCount/create',
  async (params: { stockCountTransaction: TCreatePackagingStockCount }) => {
    const { stockCountTransaction } = params;
    const response = await axios.post(
      'packaging/stock-count',
      stockCountTransaction
    );
    return response.data.data;
  }
);

export type TUpdatePackagingStockCount = TCreatePackagingStockCount;

export const updatePackagingStockCount = createAsyncThunk(
  'packagingStockCount/update',
  async (params: {
    stockCountId: string;
    stockCountTransaction: TUpdatePackagingStockCount;
  }) => {
    const { stockCountId, stockCountTransaction } = params;
    const response = await axios.put(
      `packaging/stock-count/${stockCountId}`,
      stockCountTransaction
    );
    return response.data.data;
  }
);
